import styled from 'styled-components';

import rem from 'polished/lib/helpers/rem';
import toColorString from 'polished/lib/color/toColorString';

import rgbaToHex from '@decisiv/ui-components/lib/utils/rgbaToHex';

import color from '@decisiv/design-tokens/lib/color';
import spacing from '@decisiv/design-tokens/lib/spacing';
import typography from '@decisiv/design-tokens/lib/typography';

// Real styled components rather than styled-components' `css` prop: portal's babel
// config has no babel-plugin-styled-components, so a `css={...}` would be dropped
// without warning. Same constraint the Users list works under.

// The breadcrumb strip above the header, 39px tall in the design (Figma 787:77244).
//
// A div rather than a nav: it holds KeyDS `Breadcrumbs`, which renders its own nav
// with the aria-label on it, and nesting one landmark inside another announces the
// same region twice.
export const BreadcrumbBar = styled.div`
  display: flex;
  align-items: center;
  padding: ${rem(spacing.base / 2)} ${rem(spacing.base * 2)};
  background: ${toColorString(color.base.snowWhite)};
`;

// Title, the account/role tags beneath it, and Last Login pushed to the right.
//
// Tinted rather than white — fullMoon at 50% over the white page, the same value the
// Users list uses for its filter bar, so the two pages read as one tool.
export const HeaderBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${rem(spacing.base)};
  padding: ${rem(spacing.base * 1.5)} ${rem(spacing.base * 2)};
  background: ${rgbaToHex(color.opacity.fullMoon50, color.base.snowWhite)};
  border-bottom: 1px solid ${toColorString(color.base.halfMoon)};
`;

// "Edit" sits in front of the name in a lighter weight, so the name is what the eye
// lands on (Figma 787:77246).
export const TitlePrefix = styled.span`
  font-weight: ${typography.weight.alias.regular};
`;

export const HeaderIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base / 2)};
  min-width: 0;
`;

// The pairs run in a row and wrap rather than overflow, since an account name can be
// long and there can be several.
export const AssignmentRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(spacing.base)};
`;

export const LastLogin = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 2)};
  flex-shrink: 0;
  white-space: nowrap;
`;

export const TabsArea = styled.div`
  padding: ${rem(spacing.base * 1.5)} ${rem(spacing.base * 2)} 0;
`;

// A refusal the form cannot pin to any one input — see UserUpdate's `base` key.
//
// It has to persist rather than ride the save toast, which auto-dismisses: these are
// the messages that explain why a form will not save at all, and an admin who looks
// away for eight seconds is otherwise left with a form that refuses silently. Built
// here rather than with a KeyDS banner for the reason in PlainLink — this subtree
// re-renders on every keystroke.
export const FormAlert = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base / 4)};
  margin-top: ${rem(spacing.base * 1.5)};
  padding: ${rem(spacing.base)};
  border: 1px solid ${toColorString(color.status.danger.medium)};
  border-radius: ${rem(2)};
  background-color: ${toColorString(color.status.danger.light)};
  color: ${toColorString(color.base.charcoal)};
  font-size: ${rem(typography.size.alias.paragraph2)};
`;

// Two 450px columns, 40px apart in the design (Figma 787:77249), wrapping on narrow
// viewports rather than forcing a horizontal scroll.
//
// align-items rather than the default stretch, so each column is only as tall as its
// own fields. Stretched, the shorter column is padded out to the taller one's height —
// and the two are never the same height, since the left one carries three help
// messages the right one has none of.
export const FormColumns = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${rem(spacing.base * 2.5)};
  padding: ${rem(spacing.base * 1.5)} 0;
`;

// The gap is meant to be the only thing between two fields, so each child is held to
// its natural height.
//
// KeyDS hard-codes `flex: 1` on the container it wraps every TextField and Select in
// (InputField's InputFieldContainer), which means `flex-grow: 1` and `flex-basis: 0`
// on a direct child of this column. Any spare height in the column is then shared out
// among whichever children happen to be bare fields — Address 1 and Address 2 here,
// since PhoneRow and FieldPair bring their own flex — and each grows by a third of a
// row, leaving gaps that look like a missing field. Cheaper to overrule here than to
// wrap every field in a div.
export const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base * 1.5)};
  flex: 1 1 ${rem(450)};
  max-width: ${rem(450)};

  > * {
    flex: 0 0 auto;
  }
`;

// A row holding two fields side by side: Country/State and City/Postal Code.
export const FieldPair = styled.div`
  display: flex;
  gap: ${rem(spacing.base / 2)};

  > * {
    flex: 1 1 0;
    min-width: 0;
  }
`;

// The country-code select is 130px against the number's 315px in the design.
export const PhoneRow = styled.div`
  display: flex;
  gap: ${rem(spacing.base / 2)};
  align-items: flex-start;

  > *:first-child {
    flex: 0 0 ${rem(130)};
  }

  > *:last-child {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 2)};
  padding-bottom: ${rem(spacing.base * 2)};
`;

// CANCEL leaves the page, so it is a real link rather than a button with an onClick —
// it should middle-click and open in a new tab like any other navigation. KeyDS Button
// renders only a <button> and KeyDS Link cannot survive this subtree's re-renders (see
// PlainLink), so the button appearance is built here. Same reason the Users list
// styles its own CREATE USER trigger.
export const CancelLink = styled.a`
  display: inline-flex;
  align-items: center;
  height: ${rem(32)};
  padding: 0 ${rem(spacing.base)};
  border: 1px solid ${toColorString(color.interaction.pacificOcean)};
  border-radius: ${rem(2)};
  cursor: pointer;
  text-decoration: none;
  text-transform: uppercase;
  font-size: ${rem(typography.size.alias.paragraph2)};
  font-weight: ${typography.weight.alias.semibold};
  letter-spacing: ${rem(0.5)};
  background-color: ${toColorString(color.base.snowWhite)};

  &,
  &:link,
  &:visited {
    color: ${toColorString(color.interaction.pacificOcean)};
  }

  &:hover {
    background-color: ${toColorString(color.interaction.arcticOcean40)};
    text-decoration: none;
  }
`;
